'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ITraceChartItem, ITraceChartPayload } from '@/creditflowresearch/domain/vo/Trace/ITraceChartData';
import TraceRepository from '@/creditflowresearch/infrastructure/repository/TraceRepository';
import { getContainer } from '@framework/di/getContainer';
import useRouter from '@framework/hooks/useNextRouter';
import { useFiltersContext } from '@/creditflowresearch/application/FiltersContext';
import debounce from 'lodash/debounce';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';

export const useTraceChart = (withChart: boolean) => {
  const [chartData, setChartData] = useState<ITraceChartItem[]>([]);

  const { query: { dealId } } = useRouter();
  const payload: ITraceChartPayload = useMemo(() => ({
    filter: {
      includedDealViewIds: [Number(dealId)],
    },
    search: '',
  }), []);

  const [chartPayload, setChartPayload] = useState<ITraceChartPayload>(payload);
  const [isFetching, setIsFetching] = useState(false);
  const container = getContainer();
  const traceRepository = container.get(TraceRepository);
  const { params: { search } } = useFiltersContext();

  const getTraceChartData = async () => {
    try {
      setIsFetching(true);
      const response = await traceRepository.getTraceChartData(chartPayload);
      setChartData(response);
    } catch (e) {
      if (Dispatcher.isAbortError(e)) {
        return;
      }
    } finally {
      setIsFetching(false);
    }
  };

  const updatePayloadFilter = (filter) => setChartPayload((prevState) => ({
    ...prevState,
    ...filter,
  }));

  useEffect(() => {
    if (withChart) {
      getTraceChartData();
    }
  }, [JSON.stringify(chartPayload), withChart]);

  const debouncedSearchUpdate = useCallback(debounce(updatePayloadFilter, 1000), []);

  useEffect(() => {
    debouncedSearchUpdate({ search });
  }, [search]);

  useEffect(() => {
    setChartPayload((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        includedDealViewIds: [Number(dealId)],
      },
    }));
  }, [dealId]);

  return {
    setChartData,
    chartData,
    updatePayloadFilter,
    isFetching,
  };
};

type TraceTableContextType = ReturnType<typeof useTraceChart>;

export const TraceChartContext = createContext<TraceTableContextType>(null);

export const useTraceChartContext = () => {
  const context = useContext(TraceChartContext);
  if (!context) {
    throw new Error('useTraceChartContext must be used within the TraceChartContext');
  }
  return context;
};

interface IProps {
  withChart: boolean,
  children: React.ReactNode,
}

const TraceChartContextProvider = ({ withChart, children }: IProps) => (
  <TraceChartContext.Provider value={ useTraceChart(withChart) }>
    { children }
  </TraceChartContext.Provider>
);

export default TraceChartContextProvider;
